import { useCallback, useEffect, useMemo, useState, type RefObject } from 'react';
import { isYoutubeEmbedSrc, postYoutubeIframeCommand } from './noteMediaHtml';
import { isLargePreviewGalleryMode } from './notePreviewUi';

export const LARGE_PREVIEW_DIALOG_ATTR = 'data-large-preview-dialog';

/** Where keyboard shortcuts apply in the large-preview media dialog. */
export type LargePreviewKeyboardContext = 'off' | 'away' | 'youtube' | 'gallery';

const FOCUS_POLL_MS = 200;

function isIframeInPreviewDialog(el: EventTarget | null): el is HTMLIFrameElement {
  return el instanceof HTMLIFrameElement && el.closest(`[${LARGE_PREVIEW_DIALOG_ATTR}]`) !== null;
}

function readAppFocused(): boolean {
  return document.hasFocus() && !document.hidden;
}

function computeKeyboardContext(
  open: boolean,
  mode: string,
  appFocused: boolean,
  ytEngaged: boolean,
): LargePreviewKeyboardContext {
  if (!open || !isLargePreviewGalleryMode(mode)) {
    return 'off';
  }
  if (!appFocused) {
    return 'away';
  }
  if (ytEngaged) {
    return 'youtube';
  }
  return 'gallery';
}

export type LargePreviewIframeFocusHandlers = {
  onFocus: () => void;
  onBlur: () => void;
};

export function useLargePreviewKeyboardContext(
  open: boolean,
  mode: string,
): {
  context: LargePreviewKeyboardContext;
  previewIframeFocusHandlers: LargePreviewIframeFocusHandlers;
} {
  const [appFocused, setAppFocused] = useState(readAppFocused);
  const [ytEngaged, setYtEngaged] = useState(false);

  /** Single source of truth: away when tab/window lost; purple only when app focused + YT iframe active. */
  const syncFocusState = useCallback(() => {
    const app = readAppFocused();
    setAppFocused(app);
    if (!app) {
      setYtEngaged(false);
      return;
    }
    setYtEngaged(isIframeInPreviewDialog(document.activeElement));
  }, []);

  useEffect(() => {
    if (!open || !isLargePreviewGalleryMode(mode)) {
      setYtEngaged(false);
      return;
    }

    syncFocusState();

    const onWindowBlur = () => {
      setAppFocused(false);
      setYtEngaged(false);
    };

    const onWindowFocus = () => {
      requestAnimationFrame(syncFocusState);
    };

    const onVisibilityChange = () => {
      syncFocusState();
    };

    const onFocusIn = () => {
      requestAnimationFrame(syncFocusState);
    };

    // Cross-origin YT often skips iframe onFocus; mousedown on iframe still hits the element in parent DOM.
    const onPointerDown = (e: PointerEvent) => {
      if (!readAppFocused()) {
        return;
      }
      if (isIframeInPreviewDialog(e.target)) {
        setYtEngaged(true);
        setAppFocused(true);
        return;
      }
      requestAnimationFrame(syncFocusState);
    };

    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('focus', onWindowFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    const pollId = window.setInterval(syncFocusState, FOCUS_POLL_MS);

    return () => {
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('focus', onWindowFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.clearInterval(pollId);
    };
  }, [open, mode, syncFocusState]);

  const previewIframeFocusHandlers = useMemo<LargePreviewIframeFocusHandlers>(
    () => ({
      onFocus: () => {
        if (!readAppFocused()) {
          return;
        }
        setYtEngaged(true);
        setAppFocused(true);
      },
      onBlur: () => {
        requestAnimationFrame(syncFocusState);
      },
    }),
    [syncFocusState],
  );

  const context = computeKeyboardContext(open, mode, appFocused, ytEngaged);

  return { context, previewIframeFocusHandlers };
}

type LargePreviewMediaKeyboardOptions = {
  open: boolean;
  mode: string;
  embedUrl: string;
  candidateCount: number;
  onIndexChange: (updater: (index: number) => number) => void;
  iframeRef: RefObject<HTMLIFrameElement | null>;
  gridIframeRef?: RefObject<HTMLIFrameElement | null>;
  blocked?: boolean;
  onOpenMediaPlayer?: () => void;
  onOpenMediaGrid?: () => void;
};

function resolvePlaybackIframe(
  mode: string,
  mediaIframeRef: RefObject<HTMLIFrameElement | null>,
  gridIframeRef?: RefObject<HTMLIFrameElement | null>,
): HTMLIFrameElement | null {
  if (mode === 'grid') {
    return gridIframeRef?.current ?? null;
  }
  return mediaIframeRef.current;
}

export type LargePreviewMediaKeyboard = {
  context: LargePreviewKeyboardContext;
  previewIframeFocusHandlers: LargePreviewIframeFocusHandlers;
  canYoutubeSpace: boolean;
  isYoutubePlaying: boolean;
  toggleYoutubePlayback: () => void;
};

/** Arrow keys: prev/next. Space: play/pause on active iframe (media or selected grid tile). */
export function useLargePreviewMediaKeyboard({
  open,
  mode,
  embedUrl,
  candidateCount,
  onIndexChange,
  iframeRef,
  gridIframeRef,
  blocked = false,
  onOpenMediaPlayer,
  onOpenMediaGrid,
}: LargePreviewMediaKeyboardOptions): LargePreviewMediaKeyboard {
  const [isYoutubePlaying, setIsYoutubePlaying] = useState(false);
  const { context: keyboardContext, previewIframeFocusHandlers } = useLargePreviewKeyboardContext(
    open,
    mode,
  );
  const canYoutubeSpace = Boolean(embedUrl && isYoutubeEmbedSrc(embedUrl));

  useEffect(() => {
    setIsYoutubePlaying(false);
  }, [embedUrl]);

  const toggleYoutubePlayback = useCallback(() => {
    if (!canYoutubeSpace) {
      return;
    }
    const iframe = resolvePlaybackIframe(mode, iframeRef, gridIframeRef);
    if (!iframe) {
      return;
    }
    const command = isYoutubePlaying ? 'pauseVideo' : 'playVideo';
    postYoutubeIframeCommand(iframe, command);
    setIsYoutubePlaying((playing) => !playing);
  }, [canYoutubeSpace, gridIframeRef, iframeRef, isYoutubePlaying, mode]);

  useEffect(() => {
    if (!open || !isLargePreviewGalleryMode(mode)) {
      return;
    }

    const shouldIgnoreTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) {
        return false;
      }
      const tag = target.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (keyboardContext !== 'gallery' || blocked || shouldIgnoreTarget(e.target)) {
        return;
      }

      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        if (canYoutubeSpace) {
          toggleYoutubePlayback();
        }
        return;
      }

      const key = e.key.toLowerCase();
      const plainLetter = !e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey;

      if (plainLetter && key === 'g' && mode === 'media' && onOpenMediaGrid && candidateCount > 0) {
        e.preventDefault();
        e.stopPropagation();
        onOpenMediaGrid();
        return;
      }

      if (plainLetter && key === 'o' && mode === 'grid' && onOpenMediaPlayer) {
        e.preventDefault();
        e.stopPropagation();
        onOpenMediaPlayer();
        return;
      }

      if (candidateCount <= 1) {
        return;
      }
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
        return;
      }

      e.preventDefault();
      const max = candidateCount - 1;
      if (e.key === 'ArrowLeft') {
        onIndexChange((i) => Math.max(0, i - 1));
      } else {
        onIndexChange((i) => Math.min(max, i + 1));
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [
    blocked,
    candidateCount,
    canYoutubeSpace,
    keyboardContext,
    mode,
    onIndexChange,
    onOpenMediaGrid,
    onOpenMediaPlayer,
    open,
    toggleYoutubePlayback,
  ]);

  return {
    context: keyboardContext,
    previewIframeFocusHandlers,
    canYoutubeSpace,
    isYoutubePlaying,
    toggleYoutubePlayback,
  };
}
